import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const PACKAGES = [
  { id: "small", label: "Small ($50)", desc: "Quick test charge" },
  { id: "medium", label: "Medium ($500)", desc: "Standard ticket" },
  { id: "large", label: "Large ($5,000)", desc: "Mid-tier" },
  { id: "high", label: "High Ticket ($25,000)", desc: "Approved-only flow" },
];

export default function NewTransaction() {
  const navigate = useNavigate();
  const { formatApiError } = useAuth();
  const [pkg, setPkg] = useState("medium");
  const [submitting, setSubmitting] = useState(false);
  const [echeck, setEcheck] = useState({
    customer_name: "Jane Doe", customer_email: "jane@example.com",
    routing_number: "021000021", account_number: "12345678",
    amount: 250, country: "US", description: "ACH payment",
  });
  const upd = (k) => (e) => setEcheck({ ...echeck, [k]: e.target.value });

  const stripeCheckout = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/transactions/stripe/checkout", { package_id: pkg, origin_url });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const submitEcheck = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...echeck, amount: parseFloat(echeck.amount) };
      await api.post("/transactions/echeck", payload);
      toast.success("eCheck transaction recorded");
      navigate("/dashboard/transactions");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="new-transaction-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#00D533]">Process</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">New Charge</h1>
      <p className="text-slate-500 mt-2 max-w-xl">
        Card payments redirect to Stripe Checkout. eCheck/ACH is processed via SelleCheck rails with risk underwriting.
      </p>

      <Tabs defaultValue="card" className="mt-8 max-w-2xl">
        <TabsList className="bg-[#FFFFFF] border border-slate-200">
          <TabsTrigger value="card" className="data-[state=active]:bg-slate-100" data-testid="tab-card">
            <CreditCard className="w-4 h-4 mr-2" /> Card (Stripe)
          </TabsTrigger>
          <TabsTrigger value="echeck" className="data-[state=active]:bg-slate-100" data-testid="tab-echeck">
            <Banknote className="w-4 h-4 mr-2" /> eCheck / ACH
          </TabsTrigger>
        </TabsList>

        <TabsContent value="card">
          <form onSubmit={stripeCheckout} className="sk-card p-6 mt-6 space-y-5" data-testid="stripe-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PACKAGES.map((p) => (
                <label
                  key={p.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    pkg === p.id ? "border-[#00D533] bg-[#00D533]/5" : "border-slate-200 hover:border-slate-200"
                  }`}
                  data-testid={`pkg-${p.id}`}
                >
                  <input type="radio" name="pkg" value={p.id} checked={pkg === p.id} onChange={() => setPkg(p.id)} className="sr-only" />
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
                </label>
              ))}
            </div>
            <Button type="submit" disabled={submitting} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="stripe-pay-btn">
              {submitting ? "Redirecting..." : "Continue to Stripe Checkout"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="echeck">
          <form onSubmit={submitEcheck} className="sk-card p-6 mt-6 grid sm:grid-cols-2 gap-4" data-testid="echeck-form">
            <FormInput label="Customer name" required value={echeck.customer_name} onChange={upd("customer_name")} testid="echeck-name" />
            <FormInput label="Customer email" type="email" required value={echeck.customer_email} onChange={upd("customer_email")} testid="echeck-email" />
            <FormInput label="Routing number (9 digits)" required value={echeck.routing_number} onChange={upd("routing_number")} testid="echeck-routing" />
            <FormInput label="Account number" required value={echeck.account_number} onChange={upd("account_number")} testid="echeck-account" />
            <FormInput label="Amount (USD)" type="number" step="0.01" required value={echeck.amount} onChange={upd("amount")} testid="echeck-amount" />
            <FormInput label="Country" value={echeck.country} onChange={upd("country")} testid="echeck-country" />
            <div className="sm:col-span-2">
              <Label className="text-slate-600">Description</Label>
              <Textarea value={echeck.description} onChange={upd("description")} className="mt-2 bg-[#F8FAFC] border-slate-200 min-h-[80px]" data-testid="echeck-description" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={submitting} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="echeck-submit-btn">
                {submitting ? "Processing..." : "Submit eCheck"}
              </Button>
            </div>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FormInput({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-slate-600">{label}</Label>
      <Input {...rest} className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid={testid} />
    </div>
  );
}
